import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import { checkIsInternal, checkHCO, getPSMSProviderEntityId } from '@src/utility/createStorage';
import { APPEALS_TREND_DATA_API_URL, CLAIMS_TREND_DATA_API_URL, DOP_TOP_REASON_SUB_CAT_API_URL, DOP_TOP_REASON_TOP_CAT_API_URL, NONPAYMENT_DETAILS_API_URL, TOP_REASON_SUB_CAT_API_URL, TOP_REASON_TOP_CAT_API_URL, MCR_INVENTORY_API_URL, CLAIMS_SUMMARY_API_URL, CLAIMS_APPEALS_SUMMARY_API_URL, CLAIMS_BIFURCATE_METRICS_API_URL, CLAIMS_RECON_DETAILS_API_URL, CLAIMS_YIELD_PASS_WISE_METRICS_API_URL, AUTOMATION_RATE_CLAIM_METRICS_API_URL, CLAIMS_YIELD_ALL_PASSES_METRICS_API_URL, CLAIMS_APPEALS_SUMMARY_TIN_API_URL, PSMS_OUTCOME_REASONS_API_URL, PSMS_SUBMISSION_REASONS_API_URL, PSMS_RECON_DETAILS_API_URL, CLAIMS_RECON_TAT_API_URL, CLAIMS_APPEALS_SUBMISSION_API_URL, CLAIMS_SUMMARY_DOP_API_URL } from './apiURL';
import { ClaimsPayload } from '@src/apps/psms/PSMS_Claims/ClaimsHelper';
import get from 'lodash/get';
import { ReconsiderationsOutcomeVolumeConstants } from '@src/apps/psms/PSMS_Claims/Main/OutcomeVolume/constants';

const _httpService = new HttpService();

if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL'));
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL'));
}

export const getOutcomeByVolumeBasePayload = () => {
  // Build payload using functional composition
  const payload = {
    ClaimsBy: 'DOS',
    appealsProcessing: 'Received Date',
    adjustmentLabel: 'All',
    ReconPass: 'All', // Assuming GeoMapping is always "Provider" for GEO_FLOW, adjust if needed
  };

  // Filter out undefined and empty values
  const filteredPayload = Object.fromEntries(
    Object.entries(payload).filter(
      ([_, value]) => value !== '' && value !== undefined
    )
  ) as Partial<ClaimsPayload>;

  return filteredPayload;
};

export const getClaimsTrendData = async (payload?: any) => {
  const finalUrl = CLAIMS_TREND_DATA_API_URL;
  try {
    const responseData: any = await _httpService.push(finalUrl, payload);
    return responseData;
  } catch (error) {
    return null;
  }
}

// FOR GETTING TOP REASONS DATA FOR CLAIMS NON PAYMENTS
export const getTopReasonTopCategory = async (payload: any) => {
  let url = "";
  if (payload?.ClaimsBy === 'DOP') {
    url = DOP_TOP_REASON_TOP_CAT_API_URL;
  } else {
    url = TOP_REASON_TOP_CAT_API_URL;
  }
  const finalUrl = url;
  try {
    const responseData: any = await _httpService.push(finalUrl, payload);
    return responseData;
  } catch (error) {
    return null;
  }
}

// FOR GETTING SUB-CATEGORY DATA IN TOP REASONS TABLE
export const getTopReasonSubCategory = async (payload: any) => {
  let url = "";
  if (payload?.ClaimsBy === 'DOP') {
    url = DOP_TOP_REASON_SUB_CAT_API_URL;
  } else {
    url = TOP_REASON_SUB_CAT_API_URL;
  }
  const finalUrl = url;

  try {
    const responseData: any = await _httpService.push(finalUrl, payload);
    return responseData;
  } catch (error) {
    return null;
  }
}

export const getMcrInventoryData = async (payload: any) => {
  const url = MCR_INVENTORY_API_URL;
  try {
    const response: any = await _httpService.push(url, payload);
    return response;
  } catch (error) {
    console.error('getMcrInventoryData error:', error);
    throw error;
  }
};

// FOR GETTING COMPLETE AND PARTIAL DETAILS IN NON-PAYMENT TOP REASONS TABLE
export const getNonPaymentDetails = async (payload: any) => {
  const url = NONPAYMENT_DETAILS_API_URL;
  const finalUrl = url;

  try {
    const responseData: any = await _httpService.push(finalUrl, payload);
    return responseData;
  } catch {
    return null;
  }
}

// FOR GETTING OUTCOME REASONS DATA (OUTCOME VOLUME)
export const getPsmsOutcomeReasons = async (payload: any) => {
  const updatedPayload = {
    ...payload,
    ...getOutcomeByVolumeBasePayload(), // Merge with base payload
    Count: payload?.Count || 20,
    TicketStatus: payload?.TicketStatus || ReconsiderationsOutcomeVolumeConstants.OVERTURNED,
  };
  if (updatedPayload.Tins) {
    updatedPayload.Tin = updatedPayload.Tins;
    delete updatedPayload.Tins;
  }

  try {
    const responseData: any = await _httpService.push(`${PSMS_OUTCOME_REASONS_API_URL}/`, updatedPayload);
    return responseData;
  } catch {
    return null;
  }
};

export const getClaimsReconDetails = async (payload: any) => {
  const url = CLAIMS_RECON_DETAILS_API_URL;
  try {
    const responseData: any = await _httpService.push(url, payload);
    return responseData;
  } catch {
    return null;
  }
};

// FOR GETTING SUBMISSION REASONS DATA (OUTCOME VOLUME)
export const getPsmsSubmissionReasons = async (payload: any) => {
  const updatedPayload = {
    ...payload,
    ...getOutcomeByVolumeBasePayload(), // Merge with base payload
    Count: payload?.Count || 20,
    TicketStatus: payload?.TicketStatus || ReconsiderationsOutcomeVolumeConstants.OVERTURNED,
  };
  if (updatedPayload.Tins) {
    updatedPayload.Tin = updatedPayload.Tins;
    delete updatedPayload.Tins;
  }

  try {
    const responseData: any = await _httpService.push(`${PSMS_SUBMISSION_REASONS_API_URL}/`, updatedPayload);
    return responseData;
  } catch {
    return null;
  }
}

export const getClaimsReconTatDetails = async (payload: any) => {
  const url = CLAIMS_RECON_TAT_API_URL;
  try {
    const responseData: any = await _httpService.push(url, payload);
    return responseData;
  } catch {
    return null;
  }
};

// FOR GETTING RECONSIDERATION DETAILS (OUTCOME VOLUME)
export const getPsmsReconDetails = async (payload: any) => {
  const updatedPayload = {
    ...payload,
    ...getOutcomeByVolumeBasePayload(), // Merge with base payload
  }
  if (updatedPayload.Tins) {
    updatedPayload.Tin = updatedPayload.Tins;
    delete updatedPayload.Tins;
  }

  try {
    const responseData: any = await _httpService.push(`${PSMS_RECON_DETAILS_API_URL}/`, updatedPayload);
    return responseData;
  } catch {
    return null;
  }
};



export const getClaimsSummaryData = async (payload: any) => {
  let url = "";
  if (payload?.ClaimsBy === 'DOP') {
    url = CLAIMS_SUMMARY_DOP_API_URL;
  } else {
    url = CLAIMS_SUMMARY_API_URL
  }
  const finalUrl = url;
  try {
    const response: any = await _httpService.push(finalUrl, payload);
    return response;
  } catch (error) {
    console.error('getClaimsSummaryData error:', error);
    throw error;
  }
};

export const getClaimsAppealsSummaryData = async (payload: any) => {
  const url = checkHCO() ? CLAIMS_APPEALS_SUMMARY_API_URL : CLAIMS_APPEALS_SUMMARY_TIN_API_URL;
  try {
    const response: any = await _httpService.push(url, payload);
    return response;
  } catch (error) {
    console.error('getClaimsAppealsSummaryData error:', error);
    throw error;
  }
};

export const getClaimsBifurcateMetricsData = async (payload: any) => {
  const url = CLAIMS_BIFURCATE_METRICS_API_URL;
  try {
    const response: any = await _httpService.push(url, payload);
    return response;
  } catch (error) {
    console.error('getClaimsBifurcateMetricsData error:', error);
    throw error;
  }
};

export const getClaimsReconDetailsData = async (payload: any) => {
  const url = CLAIMS_RECON_DETAILS_API_URL;
  try {
    const response: any = await _httpService.push(url, payload);
    return response;
  } catch (error) {
    console.error('getClaimsReconDetailsData error:', error);
    throw error;
  }
};

export const getClaimsYieldPassWiseMetricsData = async (payload: any) => {
  const url = CLAIMS_YIELD_PASS_WISE_METRICS_API_URL;
  try {
    const response: any = await _httpService.push(url, payload);
    return response;
  } catch (error) {
    console.error('getClaimsYieldPassWiseMetricsData error:', error);
    throw error;
  }
};

export const getClaimsYieldAllPassesMetricsData = async (payload: any) => {
  const url = CLAIMS_YIELD_ALL_PASSES_METRICS_API_URL;
  try {
    const response: any = await _httpService.push(url, payload);
    return response;
  } catch (error) {
    console.error('getClaimsYieldAllPassesMetricsData error:', error);
    throw error;
  }
};

export const getAutomationRateClaimMetricsData = async (payload: any) => {
  const url = AUTOMATION_RATE_CLAIM_METRICS_API_URL;
  try {
    const response: any = await _httpService.push(url, payload);
    return response;
  } catch (error) {
    console.error('getAutomationRateClaimMetricsData error:', error);
    throw error;
  }
};

export const getMcrEvaluationDetails = async (payload: any, requestType: string = 'ANNUAL') => {
  const url = `payment-integrity/evaluation-details/?request-type=${requestType}`;
  try {
    const response: any = await _httpService.push(url, payload);
    return response;
  } catch (error) {
    console.error('getMcrEvaluationDetails error:', error);
    throw error;
  }
};
export const getAppealTrendData = async (payload: any) => {
  const url = payload?.Tin ? `${APPEALS_TREND_DATA_API_URL}/?request-type=APPEALS_SMRY_DOR_TREND_TIN` : `${APPEALS_TREND_DATA_API_URL}/?request-type=APPEALS_SMRY_DOR_TREND_HCO`;
  try {
    const responseData: any = await _httpService.push(url, payload);
    return responseData;
  } catch {
    return null;
  }
}

export const getClaimsAppealsSubmissiondData = async (payload?: any) => {
  const finalUrl = CLAIMS_APPEALS_SUBMISSION_API_URL;
  try {
    const responseData: any = await _httpService.push(finalUrl, payload);
    return responseData;
  } catch (error) {
    return null;
  }
}

